import { useState, useEffect } from 'react'
import { SkeletonStats, SkeletonTable, SkeletonCard } from '../components/SkeletonLoader';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiBarChart2, FiEye, FiSettings, FiRefreshCw, FiBriefcase } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { empresasAPI } from '../services/api';
import { PageHeader, Btn } from '../ui';
import { formatearFecha } from '../utils/fechas';

const GestionEmpresas = () => {
  const navigate = useNavigate();
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit' | 'view'
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [mostrarInactivas, setMostrarInactivas] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todas'); // 'todas' | 'normal' | 'asesoria'
  const [formData, setFormData] = useState({
    nombre: '',
    cif: '',
    direccion: '',
    telefono: '',
    email: '',
    plan: 'pro',
    tipo: 'normal',
    fecha_expiracion: '',
    slots_total: 0,
    subscription_status: '',
    billing_interval: 'monthly',
    trial_ends_at: ''
  });

  const gestionarEmpresa = (empresa) => {
    navigate(`/admin/empresa/${empresa.id}`, { state: { empresa } });
  };

  useEffect(() => {
    cargarEmpresas();
  }, []);

  const cargarEmpresas = async () => {
    try {
      setLoading(true);
      const response = await empresasAPI.getAll();
      setEmpresas(response.data.empresas || []);
    } catch (error) {
      console.error('Error cargando empresas:', error);
      toast.error('Error al cargar empresas');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (mode, empresa = null) => {
    setModalMode(mode);
    setSelectedEmpresa(empresa);

    if (mode === 'create') {
      setFormData({
        nombre: '',
        cif: '',
        direccion: '',
        telefono: '',
        email: '',
        plan: 'pro',
        tipo: 'normal',
        fecha_expiracion: '',
        slots_total: 0,
        subscription_status: 'trialing',
        billing_interval: 'monthly',
        trial_ends_at: ''
      });
    } else if (empresa) {
      setFormData({
        nombre: empresa.nombre || '',
        cif: empresa.cif || '',
        direccion: empresa.direccion || '',
        telefono: empresa.telefono || '',
        email: empresa.email || '',
        plan: empresa.plan || 'pro',
        tipo: empresa.tipo || 'normal',
        fecha_expiracion: empresa.fecha_expiracion ? empresa.fecha_expiracion.slice(0, 16) : '',
        slots_total: empresa.slots_total || 0,
        subscription_status: empresa.subscription_status || '',
        billing_interval: empresa.billing_interval || 'monthly',
        trial_ends_at: empresa.trial_ends_at ? empresa.trial_ends_at.slice(0, 16) : ''
      });
    }

    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedEmpresa(null);
    setModalMode('create');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.nombre.trim()) {
      toast.error('El nombre de la empresa es obligatorio');
      return;
    }

    try {
      if (modalMode === 'create') {
        await empresasAPI.create(formData);
        toast.success('Empresa creada exitosamente');
      } else if (modalMode === 'edit') {
        await empresasAPI.update(selectedEmpresa.id, formData);
        toast.success('Empresa actualizada exitosamente');
      }

      handleCloseModal();
      cargarEmpresas();
    } catch (error) {
      console.error('Error guardando empresa:', error);
      toast.error(error.message || 'Error al guardar empresa');
    }
  };

  const handleDelete = async (empresa) => {
    if (empresa.id === 1) {
      toast.error('No se puede eliminar la empresa principal');
      return;
    }

    if (!window.confirm(`¿Estás seguro de desactivar la empresa "${empresa.nombre}"?`)) {
      return;
    }

    try {
      await empresasAPI.delete(empresa.id);
      toast.success('Empresa desactivada exitosamente');
      cargarEmpresas();
    } catch (error) {
      console.error('Error eliminando empresa:', error);
      toast.error(error.message || 'Error al desactivar empresa');
    }
  };

  const empresasFiltradas = empresas
    .filter(e => mostrarInactivas ? true : e.activo)
    .filter(e => {
      if (filtroTipo === 'todas') return true
      if (filtroTipo === 'asesoria') return e.tipo === 'asesoria'
      if (filtroTipo === 'cliente_interno') return e.tipo === 'cliente_interno'
      return e.tipo === 'normal' || (!e.tipo)
    });

  const handleReactivar = async (empresa) => {
    if (!window.confirm(`¿Reactivar la empresa "${empresa.nombre}"?`)) return;
    try {
      await empresasAPI.update(empresa.id, { activo: true });
      toast.success('Empresa reactivada');
      cargarEmpresas();
    } catch (error) {
      toast.error(error.message || 'Error al reactivar empresa');
    }
  };

  const getPlanBadgeColor = (plan) => {
    const colors = {
      pro: 'bg-blue-100 text-blue-800',
      avanzado: 'bg-purple-100 text-purple-800',
      trial: 'bg-yellow-100 text-yellow-800',
    };
    return colors[plan] || colors.pro;
  };

  const getPlanLabel = (plan) => {
    const labels = {
      pro: 'Pro',
      avanzado: 'Avanzado',
      trial: 'Trial',
    };
    return labels[plan] || plan;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        num="§ S1"
        kicker="Empresas"
        title="Gestión de"
        italic="empresas."
        sub="Administra todas las empresas del sistema."
        actions={<Btn variant="primary" size="sm" onClick={() => handleOpenModal('create')}><FiPlus size={14} /> Nueva empresa</Btn>}
        className="!px-0 !pt-0 !pb-5 !border-0"
      />

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--ink-2)]">Total Empresas</p>
              <p className="text-2xl font-bold text-[var(--ink)]">{empresas.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiBarChart2 className="text-blue-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--ink-2)]">Empresas Activas</p>
              <p className="text-2xl font-bold text-green-600">
                {empresas.filter(e => e.activo).length}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <FiUsers className="text-green-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--ink-2)]">Total Empleados</p>
              <p className="text-2xl font-bold text-purple-600">
                {empresas.reduce((sum, e) => sum + (e.total_empleados || 0), 0)}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <FiUsers className="text-purple-600" size={24} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--ink-2)]">Empleados Activos</p>
              <p className="text-2xl font-bold text-blue-600">
                {empresas.reduce((sum, e) => sum + (e.empleados_activos || 0), 0)}
              </p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <FiUsers className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow px-4 py-3 flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={mostrarInactivas}
            onChange={(e) => setMostrarInactivas(e.target.checked)}
            className="w-4 h-4 text-blue-600 rounded border-[var(--line-2)] focus:ring-blue-500"
          />
          <span className="text-sm font-medium text-[var(--ink-2)]">Mostrar inactivas</span>
        </label>

        <div className="flex items-center gap-1 bg-[var(--bg-2)] rounded-lg p-1">
          {[
            { value: 'todas', label: 'Todas' },
            { value: 'normal', label: 'Clientes' },
            { value: 'cliente_interno', label: 'Internos' },
            { value: 'asesoria', label: 'Asesorías' }
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => setFiltroTipo(opt.value)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                filtroTipo === opt.value
                  ? 'bg-white shadow text-[var(--ink)]'
                  : 'text-[var(--ink-3)] hover:text-[var(--ink-2)]'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <span className="text-sm text-[var(--ink-3)] ml-auto">
          {empresasFiltradas.length} empresa{empresasFiltradas.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  CIF
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Empleados
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Licencia
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-[var(--ink-3)] uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {empresasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-[var(--ink-3)]">
                    No hay empresas {mostrarInactivas ? 'registradas' : 'activas'}
                  </td>
                </tr>
              ) : (
                empresasFiltradas.map((empresa) => (
                  <tr key={empresa.id} className={`hover:bg-[var(--bg-2)] ${!empresa.activo ? 'opacity-50' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-[var(--ink)]">{empresa.nombre}</span>
                          {empresa.tipo === 'asesoria' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-teal-100 text-teal-700">
                              <FiBriefcase className="h-3 w-3" />
                              Asesoría
                            </span>
                          )}
                          {empresa.tipo === 'cliente_interno' && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-indigo-100 text-indigo-700">
                              Interno
                            </span>
                          )}

                        </div>
                        {empresa.email && (
                          <div className="text-sm text-[var(--ink-3)]">{empresa.email}</div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-[var(--ink-3)]">
                      {empresa.cif || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPlanBadgeColor(empresa.plan)}`}>
                        {getPlanLabel(empresa.plan)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-[var(--ink)]">
                        {empresa.empleados_activos || 0} activos
                      </div>
                      {empresa.slots_total > 0 && (
                        <div className="text-xs text-[var(--ink-3)]">
                          {empresa.slots_total} slots contratados
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {empresa.fecha_expiracion ? (
                        empresa.dias_para_expirar <= 0 ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                            Bloqueada
                          </span>
                        ) : empresa.dias_para_expirar <= 7 ? (
                          <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">
                            Expira en {empresa.dias_para_expirar}d
                          </span>
                        ) : (
                          <span className="text-sm text-[var(--ink-3)]">
                            {formatearFecha(empresa.fecha_expiracion)}
                          </span>
                        )
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          Activa
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        empresa.activo
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {empresa.activo ? 'Activa' : 'Inactiva'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            gestionarEmpresa(empresa);
                          }}
                          className={`inline-flex items-center px-3 py-1.5 text-white rounded-md transition-colors text-xs font-medium ${
                            empresa.tipo === 'asesoria'
                              ? 'bg-teal-600 hover:bg-teal-700'
                              : 'bg-purple-600 hover:bg-purple-700'
                          }`}
                          title="Gestionar empresa"
                        >
                          <FiSettings className="mr-1" size={14} />
                          Gestionar
                        </button>
                        <button
                          onClick={() => handleOpenModal('view', empresa)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                          title="Ver detalles"
                        >
                          <FiEye size={18} />
                        </button>
                        <button
                          onClick={() => handleOpenModal('edit', empresa)}
                          className="text-yellow-600 hover:text-yellow-900 p-1"
                          title="Editar"
                        >
                          <FiEdit2 size={18} />
                        </button>
                        {empresa.activo && (
                          <button
                            onClick={() => handleDelete(empresa)}
                            className="text-red-600 hover:text-red-900 p-1"
                            title="Desactivar empresa"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        )}
                        {!empresa.activo && (
                          <button
                            onClick={() => handleReactivar(empresa)}
                            className="text-green-600 hover:text-green-900 p-1"
                            title="Reactivar"
                          >
                            <FiRefreshCw size={18} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-[var(--ink)]">
                {modalMode === 'create' && 'Nueva Empresa'}
                {modalMode === 'edit' && 'Editar Empresa'}
                {modalMode === 'view' && 'Detalles de Empresa'}
              </h3>
              <button
                onClick={handleCloseModal}
                className="text-[var(--ink-4)] hover:text-[var(--ink-2)] transition-colors"
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Nombre */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">
                    Nombre de la Empresa *
                  </label>
                  <input
                    type="text"
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                    disabled={modalMode === 'view'}
                  />
                </div>

                {/* CIF */}
                <div>
                  <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">
                    CIF
                  </label>
                  <input
                    type="text"
                    value={formData.cif}
                    onChange={(e) => setFormData({ ...formData, cif: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={modalMode === 'view'}
                  />
                </div>

                {/* Teléfono */}
                <div>
                  <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={modalMode === 'view'}
                  />
                </div>

                {/* Email */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={modalMode === 'view'}
                  />
                </div>

                {/* Dirección */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">
                    Dirección
                  </label>
                  <textarea
                    value={formData.direccion}
                    onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                    rows={2}
                    className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={modalMode === 'view'}
                  />
                </div>

                {/* ═══ Suscripción y Facturación ═══ */}
                <div className="md:col-span-2 border-t border-[var(--line)] pt-4 mt-2">
                  <h4 className="text-sm font-bold text-[var(--ink)] mb-3">Suscripción y Facturación</h4>

                  {/* Mensaje informativo para cuentas asesoría */}
                  {formData.tipo === 'asesoria' && (
                    <div className="p-3 bg-teal-50 border border-teal-200 rounded-xl mb-3 text-sm text-teal-800">
                      <strong>Cuenta de asesoría:</strong> Sin suscripción ni facturación. Acceso Avanzado gratuito permanente.
                    </div>
                  )}
                  {formData.tipo === 'cliente_interno' && (
                    <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl mb-3 text-sm text-indigo-800">
                      <strong>Cliente interno:</strong> Cobro gestionado por banco. Sin expiración, sin Stripe. Selecciona plan y plazas.
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Tipo de cuenta */}
                    <div>
                      <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">Tipo de cuenta</label>
                      <select
                        value={formData.tipo}
                        onChange={(e) => {
                          const newTipo = e.target.value
                          setFormData({
                            ...formData,
                            tipo: newTipo,
                            ...(newTipo === 'asesoria' && {
                              plan: 'avanzado',
                              subscription_status: 'asesoria',
                              slots_total: 0,
                              billing_interval: 'monthly',
                              fecha_expiracion: '',
                              trial_ends_at: ''
                            }),
                            ...(newTipo === 'cliente_interno' && {
                              subscription_status: 'interno',
                              billing_interval: 'monthly',
                              fecha_expiracion: '',
                              trial_ends_at: ''
                            }),
                            ...(newTipo === 'normal' && {
                              subscription_status: formData.subscription_status === 'asesoria' || formData.subscription_status === 'interno' ? 'trialing' : formData.subscription_status
                            })
                          })
                        }}
                        className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={modalMode === 'view'}
                      >
                        <option value="normal">Cliente estándar</option>
                        <option value="cliente_interno">Cliente interno (sin cobro Stripe)</option>
                        <option value="asesoria">Asesoría laboral / fiscal</option>
                      </select>
                      {formData.tipo === 'asesoria' && (
                        <p className="text-xs text-teal-600 mt-1">Plan Avanzado gratuito permanente. Sin fichajes propios.</p>
                      )}
                      {formData.tipo === 'cliente_interno' && (
                        <p className="text-xs text-indigo-600 mt-1">Cobro por banco. Sin expiración ni gestión Stripe.</p>
                      )}
                    </div>

                    {/* Plan */}
                    <div>
                      <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">Plan</label>
                      <select
                        value={formData.plan}
                        onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                        className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={modalMode === 'view' || formData.tipo === 'asesoria'}
                      >
                        <option value="pro">Pro (1,50 €/emp/mes)</option>
                        <option value="avanzado">Avanzado (2,95 €/emp/mes)</option>
                      </select>
                    </div>

                    {/* Estado suscripción — oculto para asesorías */}
                    {formData.tipo === 'normal' && (
                    <div>
                      <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">Estado</label>
                      <select
                        value={formData.subscription_status}
                        onChange={(e) => setFormData({ ...formData, subscription_status: e.target.value })}
                        className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={modalMode === 'view'}
                      >
                        <option value="">Sin definir</option>
                        <option value="trialing">Trial (prueba gratuita)</option>
                        <option value="active">Activo (pagando)</option>
                        <option value="past_due">Pago pendiente</option>
                        <option value="canceled">Cancelado</option>
                        <option value="unpaid">Impagado</option>
                      </select>
                    </div>
                    )}

                    {/* Slots — visible para normal y cliente_interno */}
                    {formData.tipo !== 'asesoria' && (
                    <div>
                      <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">Plazas contratadas (slots)</label>
                      <input
                        type="number"
                        min="0"
                        value={formData.slots_total}
                        onChange={(e) => setFormData({ ...formData, slots_total: parseInt(e.target.value) || 0 })}
                        className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={modalMode === 'view'}
                      />
                      <p className="text-xs text-[var(--ink-3)] mt-1">0 = sin límite (trial)</p>
                    </div>
                    )}

                    {/* Intervalo — oculto para asesorías */}
                    {formData.tipo === 'normal' && (
                    <div>
                      <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">Facturación</label>
                      <select
                        value={formData.billing_interval}
                        onChange={(e) => setFormData({ ...formData, billing_interval: e.target.value })}
                        className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={modalMode === 'view'}
                      >
                        <option value="monthly">Mensual</option>
                        <option value="yearly">Anual</option>
                      </select>
                    </div>
                    )}

                    {/* Fin del trial — oculto para asesorías */}
                    {formData.tipo === 'normal' && (
                    <div>
                      <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">Fin del trial</label>
                      <input
                        type="datetime-local"
                        value={formData.trial_ends_at}
                        onChange={(e) => setFormData({ ...formData, trial_ends_at: e.target.value })}
                        className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={modalMode === 'view'}
                      />
                    </div>

                    )}

                    {/* Fecha expiración — visible para normal y cliente_interno (bloqueo manual por impago) */}
                    {(formData.tipo === 'normal' || formData.tipo === 'cliente_interno') && (
                    <div>
                      <label className="block text-sm font-medium text-[var(--ink-2)] mb-1">
                        {formData.tipo === 'cliente_interno' ? 'Expiración (bloqueo manual)' : 'Expiración / Próxima renovación'}
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.fecha_expiracion}
                        onChange={(e) => setFormData({ ...formData, fecha_expiracion: e.target.value })}
                        className="w-full px-3 py-2 border border-[var(--line-2)] rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={modalMode === 'view'}
                      />
                      {formData.tipo === 'cliente_interno' && (
                        <p className="text-xs text-[var(--ink-3)] mt-1">Vacío = sin expiración. Útil para bloqueo manual si hay impago.</p>
                      )}
                    </div>
                    )}
                  </div>

                  {/* Resumen económico — solo para clientes normales */}
                  {formData.tipo === 'normal' && (
                  <div className={`mt-3 p-3 rounded-lg ${
                    formData.subscription_status === 'active' ? 'bg-green-50' :
                    formData.subscription_status === 'trialing' ? 'bg-blue-50' :
                    'bg-[var(--bg-2)]'
                  }`}>
                    {(() => {
                      const price = formData.plan === 'avanzado' ? 2.95 : 1.50
                      const annualPrice = formData.plan === 'avanzado' ? 2.46 : 1.25
                      const slots = formData.slots_total || 0
                      const isYearly = formData.billing_interval === 'yearly'
                      const effectivePrice = isYearly ? annualPrice : price
                      const total = isYearly ? (effectivePrice * slots * 12) : (effectivePrice * slots)
                      const intervalLabel = isYearly ? 'año' : 'mes'

                      if (formData.subscription_status === 'active' && slots > 0) {
                        return (
                          <>
                            <p className="text-sm font-medium text-green-800">
                              Próximo pago: <strong>{total.toFixed(2)} €/{intervalLabel}</strong>
                              <span className="font-normal text-green-600 ml-1">({slots} plazas × {effectivePrice.toFixed(2)} €)</span>
                            </p>
                            {formData.fecha_expiracion && (
                              <p className="text-xs text-green-600 mt-1">
                                Renovación: {formatearFecha(formData.fecha_expiracion)}
                              </p>
                            )}
                          </>
                        )
                      }

                      if (formData.subscription_status === 'trialing') {
                        return (
                          <>
                            <p className="text-sm text-blue-800">
                              <strong>Periodo de prueba</strong> — Sin plazas contratadas (ilimitadas durante el trial)
                            </p>
                            <p className="text-xs text-blue-600 mt-1">
                              Al activar plan: desde {price.toFixed(2)} €/plaza/mes ({annualPrice.toFixed(2)} € en pago anual)
                            </p>
                            {formData.trial_ends_at && (
                              <p className="text-xs text-blue-600">
                                Trial termina: {formatearFecha(formData.trial_ends_at)}
                              </p>
                            )}
                          </>
                        )
                      }

                      return (
                        <p className="text-sm text-[var(--ink-2)]">
                          {formData.subscription_status === 'canceled' ? 'Suscripción cancelada' :
                           formData.subscription_status === 'past_due' ? 'Pago pendiente' :
                           'Sin suscripción activa'}
                          {slots > 0 && ` — ${slots} plazas × ${effectivePrice.toFixed(2)} € = ${total.toFixed(2)} €/${intervalLabel}`}
                        </p>
                      )
                    })()}
                  </div>
                  )} {/* fin resumen económico */}
                </div>
              </div>

              {/* Buttons */}
              {modalMode !== 'view' && (
                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="flex-1 px-4 py-2 border border-[var(--line-2)] text-[var(--ink-2)] rounded-md hover:bg-[var(--bg-2)] transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    {modalMode === 'create' ? 'Crear Empresa' : 'Guardar Cambios'}
                  </button>
                </div>
              )}

              {modalMode === 'view' && (
                <div className="pt-4">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="w-full px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              )}
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GestionEmpresas;
